import { TypedDocumentNode } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Mutation, MutationBlockNfcCardArgs, Query, QueryGetVisitorByIdArgs } from 'graphql/generated/graphqlTypes';
import { BLOCK_NFC_CARD } from 'graphql/mutations';
import { GET_CARDS } from 'graphql/queries';
import { GET_VISITOR_DETAILS } from 'graphql/v2/queries';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import useMobileDeviceRegistrationSubscription from 'hooks/useMobileDeviceRegistrationSubscription';
import { useEffect, useState } from 'react';
import { ignoreHandledMutationError } from 'utils/Mutation/ignoreHandledMutationError';
import { getCardsByVisitorId } from 'utils/NfcCard/nfcCard';
import {
  getVisitorMobileAccessAppStatus,
  getVisitorMobileConfigAppStatus,
  isVisitorHasInstallerRole
} from 'utils/Visitor/visitor';
import { TranslatedHookProps } from '../../../shared/types';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_VISITOR_DETAILS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorById'>,
  QueryGetVisitorByIdArgs
> = GET_VISITOR_DETAILS;
const GET_CARDS_TYPED: TypedDocumentNode<Pick<Query, 'getNfcCards'>> = GET_CARDS;
const BLOCK_NFC_CARD_TYPED: TypedDocumentNode<
  Pick<Mutation, 'blockNfcCard'>,
  MutationBlockNfcCardArgs
> = BLOCK_NFC_CARD;

interface Props extends TranslatedHookProps {
  visitorId: string;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 6000;

const useAccess = ({ handleFetchError, translation, visitorId }: Props) => {
  // Apollo 4 notifies on network status changes by default, which flips `loading` to true on every refetch or
  // poll. Keep it off so a refetch does not blank content that is already on screen.
  const {
    loading: visitorDetailsLoading,
    data: visitorDetailsData,
    error: visitorDetailsError,
    refetch: refetchVisitorDetails,
    startPolling: startPollingVisitorDetails,
    stopPolling: stopPollingVisitorDetails
  } = useQuery(GET_VISITOR_DETAILS_TYPED, {
    variables: { id: visitorId },
    notifyOnNetworkStatusChange: false
  });

  const visitorDetails = visitorDetailsData?.getVisitorById;

  useEffect(() => {
    if (visitorDetailsError) {
      handleFetchError(translation.user_details_error_fetching);
      console.error(visitorDetailsError);
    }
  }, [visitorDetailsError]);

  const rawMobileDeviceConfigApp = visitorDetails ? getVisitorMobileConfigAppStatus(visitorDetails) : undefined;
  const rawMobileDeviceAccessApp = visitorDetails ? getVisitorMobileAccessAppStatus(visitorDetails) : undefined;
  const hasInstallerRole = !!visitorDetails && isVisitorHasInstallerRole(visitorDetails);

  // Both mobile devices rows can briefly disappear because of a stale/out-of-order background response
  // (polling and the mobile-device-registered subscription both refetch this query independently).
  // Once we've received a device data, keep showing it rather than flickering back to "waiting". The CONFIG
  // device is also gated on the installer role, so losing the role or switching visitor clears it.
  const [mobileDeviceConfigApp, setMobileDeviceConfigApp] = useState(rawMobileDeviceConfigApp);
  const [mobileDeviceAccessApp, setMobileDeviceAccessApp] = useState(rawMobileDeviceAccessApp);

  useEffect(() => {
    if (!visitorDetails || !hasInstallerRole) {
      setMobileDeviceConfigApp(undefined);
      return;
    }

    if (rawMobileDeviceConfigApp) setMobileDeviceConfigApp(rawMobileDeviceConfigApp);
  }, [visitorDetails, hasInstallerRole, rawMobileDeviceConfigApp]);

  useEffect(() => {
    if (!visitorDetails) {
      setMobileDeviceAccessApp(undefined);
      return;
    }

    if (rawMobileDeviceAccessApp) setMobileDeviceAccessApp(rawMobileDeviceAccessApp);
  }, [visitorDetails, rawMobileDeviceAccessApp]);

  const isWaitingForConfigDevice = hasInstallerRole && !mobileDeviceConfigApp;

  // The device just disconnected is unregistered asynchronously on the backend, so the refetch that
  // follows the mutation can still return isRegistered: true. Track it and poll briefly until it flips.
  const [awaitingDisconnectDeviceId, setAwaitingDisconnectDeviceId] = useState<string | null>(null);

  useEffect(() => {
    if (!awaitingDisconnectDeviceId) return;

    // A device missing from a given raw response can be a stale/out-of-order background response (see
    // comment above), not a real state change, so only stop waiting on a confirmed isRegistered: false.
    const matchedDevice =
      (rawMobileDeviceAccessApp?.id === awaitingDisconnectDeviceId && rawMobileDeviceAccessApp) ||
      (rawMobileDeviceConfigApp?.id === awaitingDisconnectDeviceId && rawMobileDeviceConfigApp) ||
      undefined;

    if (matchedDevice && !matchedDevice.isRegistered) setAwaitingDisconnectDeviceId(null);
  }, [awaitingDisconnectDeviceId, rawMobileDeviceAccessApp, rawMobileDeviceConfigApp]);

  const isPolling = isWaitingForConfigDevice || !!awaitingDisconnectDeviceId;

  // Polls while either: the CONFIG mobile device row is provisioned asynchronously after a visitor becomes
  // an installer, so it may not exist yet on the refetch that follows the edit; or a device disconnect is
  // awaiting backend confirmation (see awaitingDisconnectDeviceId above). Poll briefly until it resolves.
  useEffect(() => {
    if (!isPolling) {
      stopPollingVisitorDetails();
      return;
    }

    startPollingVisitorDetails(POLL_INTERVAL_MS);

    const timeout = setTimeout(() => {
      stopPollingVisitorDetails();
      setAwaitingDisconnectDeviceId(null);
    }, POLL_TIMEOUT_MS);

    return () => {
      clearTimeout(timeout);
      stopPollingVisitorDetails();
    };
  }, [isPolling, startPollingVisitorDetails, stopPollingVisitorDetails]);

  useRegisterBackgroundRefetch(isPolling);

  useMobileDeviceRegistrationSubscription(visitorId, refetchVisitorDetails);

  const {
    loading: nfcCardsLoading,
    data: nfcCardsData,
    error: nfcCardsError
  } = useQuery(GET_CARDS_TYPED, { notifyOnNetworkStatusChange: false });

  useEffect(() => {
    if (nfcCardsError) {
      handleFetchError(translation.nfc_cards_error_fetching);
      console.error(nfcCardsError);
    }
  }, [nfcCardsError]);

  const [blockNfcCardMutation, { loading: blockNfcCardLoading, data: blockNfcCardData }] = useMutation(
    BLOCK_NFC_CARD_TYPED,
    {
      refetchQueries: [GET_CARDS_TYPED],
      awaitRefetchQueries: true,
      onError: (error) => {
        handleFetchError(translation.access_card_error_blocking);
        console.error(error);
      }
    }
  );

  const blockNfcCard = async (reason: string, uuid: string) => {
    // Failure is already handled in the mutation's onError, so the rejection is ignored here.
    await ignoreHandledMutationError(
      blockNfcCardMutation({
        variables: {
          reason,
          uuid
        }
      })
    );
  };

  const startAwaitingDisconnect = (mobileDeviceId: string) => setAwaitingDisconnectDeviceId(mobileDeviceId);

  return {
    visitorDetails,
    loadingVisitorDetails: visitorDetailsLoading,
    isWaitingForConfigDevice,
    awaitingDisconnectDeviceId,
    startAwaitingDisconnect,
    mobileDeviceConfigApp,
    mobileDeviceAccessApp,
    visitorCards: getCardsByVisitorId(visitorId, nfcCardsData?.getNfcCards || []),
    loadingVisitorCards: nfcCardsLoading,
    blockNfcCard,
    blockNfcCardLoading
  };
};

export default useAccess;
